import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useMe } from '../hooks/useMe';
import { useSucursalesEstado } from '../hooks/useSucursales';
import { setSucursalContexto } from '../services/api';

const Context = createContext(null);
// Valor de la sucursal global que significa "todas las sucursales" (solo administrador).
export const TODAS = 'all';

// Sucursal elegida por el administrador: se recuerda por usuario (uid) en este navegador, también "Todas".
// Nunca se confía en lo guardado: al cargar se valida contra las sucursales que el servidor devuelve a ESTE usuario.
export const claveSucursal = (uid) => `sucursalActiva:${uid}`;
const leer = (uid) => { try { return localStorage.getItem(claveSucursal(uid)) || ''; } catch { return ''; } };
const guardar = (uid, id) => { try { localStorage.setItem(claveSucursal(uid), id); } catch { /* sin almacenamiento: solo en memoria */ } };
const olvidar = (uid) => { try { localStorage.removeItem(claveSucursal(uid)); } catch { /* idem */ } };

export function SucursalActivaProvider({ children }) {
  const me = useMe();
  // Las sucursales se piden recién con un perfil válido (sesión autenticada).
  const { sucursales, carga, recargar } = useSucursalesEstado({ activo: me.loaded });
  const isAdmin = me.role === 'admin';
  const [adminSucursalId, setAdminSucursalId] = useState('');

  // Administrador: restaura la última sucursal elegida si sigue existiendo y la puede ver; si no, la olvida y
  // empieza en "Todas las sucursales" (valor inicial del sistema). Si la lista no se pudo cargar, no se borra lo guardado.
  useEffect(() => {
    if (!me.loaded || !isAdmin || carga === 'cargando') return;
    setAdminSucursalId((prev) => {
      const valida = (id) => id === TODAS || sucursales.some((s) => s.id === id);
      if (prev && valida(prev)) return prev;
      const guardada = leer(me.uid);
      if (guardada && valida(guardada)) return guardada;
      if (guardada && carga === 'ok') olvidar(me.uid);
      return TODAS;
    });
  }, [me.loaded, me.uid, isAdmin, sucursales, carga]);

  // Cajero: una o varias sucursales asignadas (usuario_sucursales). Elige la actual entre ellas y se recuerda por usuario;
  // lo guardado solo vale si sigue asignada. Sin elección: la principal o la primera. Nunca "Todas".
  const [cajeroSel, setCajeroSel] = useState('');
  const asignadas = useMemo(() => (isAdmin ? [] : (me.sucursales?.length ? me.sucursales : me.sucursalId ? [me.sucursalId] : [])),
    [isAdmin, me.sucursales, me.sucursalId]);
  const sucursalesCajero = useMemo(() => sucursales.filter(s => asignadas.includes(s.id)), [sucursales, asignadas]);
  const asignada = (id) => !!id && sucursalesCajero.some(s => s.id === id);
  const sucursalCajero = isAdmin ? '' : [cajeroSel, leer(me.uid), me.sucursalId].find(asignada) || sucursalesCajero[0]?.id || me.sucursalId || '';
  const sucursalId = isAdmin ? adminSucursalId : sucursalCajero;
  // Lista para las pantallas: el administrador espera a que se restaure su sucursal, así no se consulta primero
  // "Todas" y luego la sucursal guardada. El cajero queda limitado a sus sucursales asignadas.
  // Sesión lista = perfil (uid, rol, sucursal asignada) + sucursales autorizadas + sucursal global restaurada.
  const listo = me.loaded && carga === 'ok' && (!isAdmin || adminSucursalId !== '');
  // Estado de la sesión para la app: 'loading' | 'authenticated' | 'unauthenticated' | 'error'.
  const estadoSesion = me.estado === 'unauthenticated' ? 'unauthenticated'
    : me.estado === 'error' || (me.loaded && carga === 'error') ? 'error' : listo ? 'authenticated' : 'loading';
  const reintentar = useCallback(() => { if (me.estado === 'error') me.reintentar(); else recargar(); }, [me, recargar]);
  // Se fija durante el render (no en un efecto) para que las pantallas hijas ya consulten con la sucursal correcta.
  // También para el cajero: la API valida que la sucursal enviada sea una de las suyas.
  setSucursalContexto(sucursalId);
  const setSucursalId = useCallback(id => {
    if (!isAdmin) {
      if (!sucursalesCajero.some(s => s.id === id)) return;
      setCajeroSel(id); guardar(me.uid, id);
      return;
    }
    if (id !== TODAS && !sucursales.some(s => s.id === id)) return;
    setAdminSucursalId(id);
    guardar(me.uid, id);
  }, [isAdmin, sucursales, sucursalesCajero, me.uid]);
  // Objeto completo de la sucursal activa (nombre, logoUrl…) para encabezados y menú.
  const sucursalActiva = useMemo(() => sucursales.find(s => s.id === sucursalId) || null, [sucursales, sucursalId]);
  // todas: el administrador eligió "Todas las sucursales" (vistas consolidadas). El cajero nunca.
  const todas = isAdmin && sucursalId === TODAS;
  const nombreSucursal = todas ? 'Todas las sucursales' : (sucursalActiva?.nombre || '');
  // Sucursales que las pantallas pueden ofrecer: todas en vista global; solo la actual si hay una elegida.
  const sucursalesDisponibles = useMemo(() => (todas ? sucursales : sucursales.filter(s => s.id === sucursalId)), [todas, sucursales, sucursalId]);
  // sucursales: el administrador ve todas; el cajero, solo sus asignadas (las de su selector).
  const visibles = isAdmin ? sucursales : sucursalesCajero;
  const value = useMemo(() => ({ sucursalId, sucursalActiva, setSucursalId, sucursales: visibles, sucursalesDisponibles, isAdmin, me, loaded: me.loaded, listo, estadoSesion, reintentar, todas, nombreSucursal }),
    [sucursalId, sucursalActiva, setSucursalId, visibles, sucursalesDisponibles, isAdmin, me, listo, estadoSesion, reintentar, todas, nombreSucursal]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSucursalActiva() {
  const value = useContext(Context);
  if (!value) throw new Error('useSucursalActiva requiere SucursalActivaProvider');
  return value;
}
